import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, Statistic, Button, Row, Col, Modal, Form, Input, InputNumber, Select, Space, Tag, message, Popconfirm, DatePicker } from 'antd';
import { PlusOutlined, ReloadOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiFetch, onDataChanged } from '../utils/api.js';
import { yuan } from '../utils/format.js';
import { prodCode } from '../utils/prodCode.js';

export default function Inventory() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ total_sku: 0, low_count: 0, total_stock: 0 });
  const [products, setProducts] = useState([]);
  const [addModal, setAddModal] = useState(null);
  const [adjModal, setAdjModal] = useState(null);
  const [form] = Form.useForm();
  const [adjForm] = Form.useForm();
  // 库存变动记录
  const [logsModal, setLogsModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logFilter, setLogFilter] = useState({ spec_code: '', type: '', source: '' });
  const [logPage, setLogPage] = useState(1);
  const [logDate, setLogDate] = useState(null);

  const loadLogs = useCallback((p, filter) => {
    const f = filter || logFilter;
    const q = new URLSearchParams();
    if (f.spec_code) q.set('spec_code', f.spec_code);
    if (f.type) q.set('type', f.type);
    if (f.source) q.set('source', f.source);
    q.set('page', p || 1);
    q.set('pageSize', 30);
    apiFetch('/api/inventory-logs?' + q.toString()).then(r => r.json()).then(d => {
      // 按日期过滤（前端）
      let list = d.data || [];
      if (logDate) list = list.filter(l => (l.time || '').slice(0, 10) === logDate.format('YYYY-MM-DD'));
      setLogs(list); setLogsTotal(d.total || 0); setLogPage(p || 1);
    }).catch(() => {});
  }, [logFilter, logDate]);
  const openLogs = () => { setLogsModal(true); setLogFilter({ spec_code: '', type: '', source: '' }); setLogDate(null); loadLogs(1, { spec_code: '', type: '', source: '' }); };
  const applyLogFilter = () => { setLogPage(1); loadLogs(1, logFilter); };

  const loadData = useCallback(() => {
    apiFetch('/api/inventory').then(r => r.json()).then(d => { setList(d.data || []); setStats(d.stats || {}); }).catch(() => {});
    apiFetch('/api/products').then(r => r.json()).then(d => setProducts(d.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => onDataChanged(() => { loadData(); }), [loadData]);

  const submitAdd = async () => {
    const v = await form.validateFields();
    const res = await apiFetch('/api/inventory', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ spec_code: v.spec_code, product_name: v.product_name, quantity: v.quantity }) }).then(r => r.json());
    if (res.success) { message.success('已添加库存'); setAddModal(null); form.resetFields(); loadData(); }
    else message.error(res.error || '添加失败');
  };
  const submitAdj = async () => {
    const v = await adjForm.validateFields();
    const res = await apiFetch('/api/inventory/adjust', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ spec_code: v.spec_code, mode: v.mode, quantity: v.quantity }) }).then(r => r.json());
    if (res.success) { message.success('已调整'); setAdjModal(null); adjForm.resetFields(); loadData(); }
    else message.error(res.error || '调整失败');
  };

  const columns = [
    { title: '商品编码', dataIndex: 'spec_code', width: 180, fixed: 'left', render: v => <b>{v}</b> },
    { title: '商品名称', dataIndex: 'product_name', ellipsis: true },
    { title: '当前库存', dataIndex: 'stock', width: 120, align: 'right',
      render: (v, r) => <span style={{ color: Number(v) < 5000 ? '#CF1322' : '#333', fontWeight: 700, fontSize: 15 }}>{yuan(v)}</span> },
    { title: '单价(元)', dataIndex: 'unit_cost', width: 100, align: 'right', render: v => v ? `¥${Number(v).toFixed(4)}` : <span style={{ color: '#bfbfbf' }}>—</span> },
    { title: '金额(元)', key: 'amount', width: 130, align: 'right',
      render: (_, r) => <span style={{ color: 'var(--brand-strong)', fontWeight: 700 }}>¥{yuan(Number(r.stock) * Number(r.unit_cost || 0))}</span> },
    { title: '初始库存', dataIndex: 'initial_stock', width: 100, align: 'right' },
    { title: '状态', width: 110, align: 'center',
      render: (_, r) => Number(r.stock) < 5000 ? <Tag color="#CF1322"><b>低库存</b></Tag> : <Tag color="#3F8600">充足</Tag> },
    { title: '创建时间', dataIndex: 'created_at', width: 110 },
    { title: '更新时间', dataIndex: 'updated_at', width: 110 },
    { title: '操作', width: 160, align: 'center', fixed: 'right',
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { adjForm.setFieldsValue({ spec_code: r.spec_code, mode: 'set', quantity: r.stock }); setAdjModal(r); }}>调整</Button>
          <Popconfirm title={`删除库存项 ${r.spec_code}？`} onConfirm={async () => {
            const res = await apiFetch('/api/inventory/' + encodeURIComponent(r.spec_code), { method: 'DELETE' }).then(x => x.json());
            if (res.success) { message.success('已删除'); loadData(); } else message.error(res.error || '删除失败');
          }}>
            <Button size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ) },
  ];

  // 商品选项：按产品编码（上级）去重，入库/采购只选上级编码
  const prodOptMap = {};
  products.forEach(p => { const k = prodCode(p.spec_code); if (!prodOptMap[k]) prodOptMap[k] = { name: p.product_name || k }; });
  const productOptions = Object.entries(prodOptMap).map(([code, v]) => ({ value: code, label: `${code}（${v.name}）` }));

  // 快速查找：按编码或名称关键词过滤（本地即时过滤）
  const [keyword, setKeyword] = useState('');
  const filteredList = keyword.trim()
    ? list.filter(i => String(i.spec_code || '').includes(keyword.trim()) || String(i.product_name || '').includes(keyword.trim()))
    : list;

  // 排序：code=按编码前置 / time=按创建时间（更新时间，由近及远=新→旧）/ stock=按库存（多→少）
  const [sortBy, setSortBy] = useState('code');
  const sortedList = useMemo(() => {
    const arr = [...filteredList];
    if (sortBy === 'stock') {
      arr.sort((a, b) => Number(b.stock) - Number(a.stock));
    } else if (sortBy === 'time') {
      // 由近及远：按编码创建时间（created_at），日期新（大）的排前面；无日期（空）的排最后
      arr.sort((a, b) => {
        const ta = String(a.created_at || ''), tb = String(b.created_at || '');
        if (!ta && !tb) return 0;
        if (!ta) return 1;
        if (!tb) return -1;
        return ta < tb ? 1 : ta > tb ? -1 : 0;
      });
    } else {
      arr.sort((a, b) => String(a.spec_code).localeCompare(String(b.spec_code)));
    }
    return arr;
  }, [filteredList, sortBy]);

  return (
    <div className="page-container">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': 'var(--brand)', padding: '16px 18px' }}>
            <div className="stat-label">库存商品款数</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{stats.total_sku}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>个</span></div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': '#CF1322', padding: '16px 18px' }}>
            <div className="stat-label">低库存(&lt;5000)</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#CF1322' }}>{stats.low_count}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>个</span></div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': '#378ADD', padding: '16px 18px' }}>
            <div className="stat-label">总库存量</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{stats.total_stock}</div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': 'var(--brand-strong)', padding: '16px 18px' }}>
            <div className="stat-label">库存总金额(元)</div>
            <div className="stat-value" style={{ fontSize: 22, color: 'var(--brand-strong)' }}>¥{yuan(stats.total_amount)}</div>
          </div>
        </Col>
      </Row>

      <Card
        title="库存管理（出库按每日订单发货自动扣减，低于 5000 红色预警）"
        extra={
          <Space wrap>
            <Input.Search placeholder="快速查找：编码 / 名称" allowClear style={{ width: 220 }} value={keyword}
              onChange={e => setKeyword(e.target.value)} onSearch={v => setKeyword(v)} />
            <Button icon={<HistoryOutlined />} onClick={openLogs}>变动记录</Button>
            <Button icon={<PlusOutlined />} type="primary" onClick={() => { form.resetFields(); setAddModal({}); }}>添加库存</Button>
            <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
            <Select value={sortBy} onChange={setSortBy} style={{ width: 130 }}
              options={[
                { value: 'code', label: '按编码前置' },
                { value: 'time', label: '按创建时间' },
                { value: 'stock', label: '按库存' },
              ]} />
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Table rowKey="spec_code" columns={columns} dataSource={sortedList} scroll={{ x: 1010 }} pagination={{ pageSize: 20 }} size="small" />
      </Card>

      <Modal title="添加库存（首次=初始库存，再次=追加数量）" open={!!addModal} onOk={submitAdd} onCancel={() => setAddModal(null)}>
        <Form form={form} layout="vertical">
          <Form.Item name="spec_code" label="商品编码" rules={[{ required: true }]}>
            <Select showSearch options={productOptions} filterOption={(i, o) => (o.label || '').includes(i)} placeholder="选择商品编码"
              onChange={(v) => {
                const p = products.find(x => prodCode(x.spec_code) === v) || products.find(x => x.spec_code === v);
                form.setFieldsValue({ product_name: p ? p.product_name : '' });
              }} />
          </Form.Item>
          <Form.Item name="product_name" label="商品名称">
            <Input placeholder="选择编码后自动带出，也可手动修改" />
          </Form.Item>
          <Form.Item name="quantity" label="库存数量" rules={[{ required: true }]}>
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="数量（件）" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={`调整库存：${adjModal ? adjModal.spec_code : ''}`} open={!!adjModal} onOk={submitAdj} onCancel={() => setAdjModal(null)}>
        <Form form={adjForm} layout="vertical">
          <Form.Item name="spec_code" label="商品编码" hidden><Input /></Form.Item>
          <Form.Item name="mode" label="调整方式">
            <Select options={[{ value: 'set', label: '直接设置为' }, { value: 'add', label: '增减（可负）' }]} />
          </Form.Item>
          <Form.Item name="quantity" label="数量" rules={[{ required: true }]}>
            <InputNumber precision={0} style={{ width: '100%' }} placeholder="数量（直接设置=目标值，增减=增减值）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 库存变动记录 */}
      <Modal title="库存变动记录（出入库流水）" open={logsModal} footer={null} width={720} onCancel={() => setLogsModal(false)}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input placeholder="产品编码筛选" allowClear style={{ width: 160 }} value={logFilter.spec_code}
            onChange={e => setLogFilter({ ...logFilter, spec_code: e.target.value })} onPressEnter={applyLogFilter} />
          <Select placeholder="类型" allowClear style={{ width: 100 }} value={logFilter.type || undefined}
            onChange={v => setLogFilter({ ...logFilter, type: v || '' })} options={[{ value: 'in', label: '入库' }, { value: 'out', label: '出库' }]} />
          <Select placeholder="来源" allowClear style={{ width: 150 }} value={logFilter.source || undefined}
            onChange={v => setLogFilter({ ...logFilter, source: v || '' })}
            options={['订单出库', '采购入库', '采购入库调整', '手动入库', '删除手动入库', '删除采购入库', '手工调整'].map(s => ({ value: s, label: s }))} />
          <DatePicker placeholder="日期" value={logDate} onChange={setLogDate} />
          <Button type="primary" onClick={applyLogFilter}>筛选</Button>
          <Button onClick={() => { setLogFilter({ spec_code: '', type: '', source: '' }); setLogDate(null); loadLogs(1, { spec_code: '', type: '', source: '' }); }}>重置</Button>
          <span style={{ color: '#8c8c8c' }}>共 {logsTotal} 条</span>
        </Space>
        <Table size="small" rowKey="id" dataSource={logs} pagination={{ pageSize: 20, current: logPage, total: logsTotal, onChange: (p) => loadLogs(p) }}
          columns={[{
            title: '变动记录', dataIndex: '_render',
            render: (_, r) => (
              <div style={{ padding: '4px 0' }}>
                <div style={{ marginBottom: 4 }}>
                  <b style={{ fontFamily: 'monospace', color: 'var(--brand-strong)' }}>{r.spec_code}</b>
                  <span style={{ color: '#595959', marginLeft: 6 }}>{r.product_name}</span>
                  <Tag color={r.type === 'in' ? 'green' : 'red'} style={{ marginLeft: 8 }}>{r.type === 'in' ? '入库' : '出库'}</Tag>
                </div>
                <div style={{ fontSize: 12, color: '#595959', marginBottom: 4 }}>
                  时间：{r.time}
                </div>
                <div style={{ marginBottom: 4 }}>
                  数量 <b style={{ color: r.type === 'in' ? '#3F8600' : '#CF1322' }}>{r.type === 'in' ? '+' : ''}{r.qty}</b>
                  <span style={{ marginLeft: 16 }}>变动后库存 <b>{r.balance}</b></span>
                </div>
                <div style={{ fontSize: 12, color: '#595959' }}>
                  来源：<Tag color="#378ADD" style={{ marginInline: 4 }}>{r.source}</Tag>
                  关联单号：{r.ref || '—'}
                  <span style={{ marginLeft: 12 }}>操作人：{r.operator || '—'}</span>
                </div>
              </div>
            ),
          }]} />
      </Modal>
    </div>
  );
}
